import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../store/store";
import {
  canFetchTeamScopedProfiles,
  resolveCurrentTeamId,
  resolveProfilesQueryTeamId,
} from "../utils/active-team/active-team";
import {
  selectIsAuthorizationFeatureEnabled,
  selectIsPermissionsLoaded,
} from "./usePermissions";

export const useActiveTeamContext = (): {
  currentTeamId: string | null;
  profilesQueryTeamId: string | null;
  canFetchTeamScopedProfiles: boolean;
  isAuthorizationEnabled: boolean;
} => {
  const isAuthorizationEnabled = useSelector(selectIsAuthorizationFeatureEnabled);
  const isPermissionsLoaded = useSelector(selectIsPermissionsLoaded);
  const permissionsTeamId = useSelector(
    (state: RootState) => state.permissions.teamId
  );
  const preferredTeamId = useSelector(
    (state: RootState) => state.permissions.preferredTeamId
  );

  const resolutionInput = useMemo(
    () => ({ permissionsTeamId, preferredTeamId }),
    [permissionsTeamId, preferredTeamId]
  );

  const currentTeamId = useMemo(
    () => resolveCurrentTeamId(resolutionInput),
    [resolutionInput]
  );

  const profilesQueryTeamId = useMemo(
    () => resolveProfilesQueryTeamId(resolutionInput),
    [resolutionInput]
  );

  const canFetchProfiles = useMemo(
    () =>
      canFetchTeamScopedProfiles(
        isAuthorizationEnabled,
        isPermissionsLoaded,
        profilesQueryTeamId
      ),
    [isAuthorizationEnabled, isPermissionsLoaded, profilesQueryTeamId]
  );

  return {
    currentTeamId,
    profilesQueryTeamId,
    canFetchTeamScopedProfiles: canFetchProfiles,
    isAuthorizationEnabled,
  };
};
